import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface HeroCarouselProps {
  images: string[];
  /** Период смены центрального кадра, мс. По умолчанию 4500. */
  interval?: number;
}

/**
 * Cover-flow карусель: центр в фокусе, по бокам - затенённые/блёрнутые соседи.
 * Каждые `interval` мс активный кадр сменяется (вправо по кругу).
 *
 * Список картинок дублируется внутри (`[...images, ...images]`), чтобы образовать
 * кольцо длины 2N - за счёт этого «вход справа» и «выход слева» получают свои
 * отдельные слоты и не возникает визуальный «скачок» через весь экран при wrap.
 */
export function HeroCarousel({ images, interval = 4500 }: HeroCarouselProps) {
  const ring = images.length === 0 ? [] : [...images, ...images];
  const N = ring.length;
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (N <= 1) return;
    const id = setInterval(() => setActive((v) => (v + 1) % N), interval);
    return () => clearInterval(id);
  }, [N, interval]);

  if (N === 0) return null;

  return (
    <div className="relative aspect-[4/3] [perspective:1400px]">
      {ring.map((src, i) => {
        const off = (i - active + N) % N;

        // 5 видимых слотов + общий hidden. Wrap происходит между «far-right» и «far-left»,
        // оба невидимы (opacity-0), поэтому переход незаметен.
        let cls = 'opacity-0 scale-50 translate-x-0 z-0';
        if (off === 0) {
          cls = 'opacity-100 scale-100 translate-x-0 z-30';
        } else if (off === 1) {
          cls =
            'opacity-60 scale-[0.8] translate-x-[45%] z-20 [filter:brightness(0.65)_blur(2px)]';
        } else if (off === N - 1) {
          cls =
            'opacity-60 scale-[0.8] -translate-x-[45%] z-20 [filter:brightness(0.65)_blur(2px)]';
        } else if (off === 2) {
          cls = 'opacity-0 scale-[0.6] translate-x-[90%] z-10';
        } else if (off === N - 2) {
          cls = 'opacity-0 scale-[0.6] -translate-x-[90%] z-10';
        }

        return (
          <div
            key={i}
            aria-hidden={off !== 0}
            className={cn(
              'absolute inset-0 rounded-2xl bg-cover bg-center shadow-[0_35px_80px_-20px_rgba(0,0,0,0.85)] ring-1 ring-white/5 transition-all duration-700 ease-out',
              cls,
            )}
            style={{ backgroundImage: `url(${src})` }}
          />
        );
      })}
    </div>
  );
}
